import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader, IActionButtonInfo } from '../../../../framework/components/bwj-page-header';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { AppRoutes } from '../../../app-routes';
import { BwjButtonType } from '../../../../framework/components/bwj-button';
import { ITimeBlockListing } from '../../../../entities-generated/models/time-block/time-block-listing';
import { CreateTimeBlocksPanel } from '../../components/create-time-panel';
import { ChangeTimeBlocksPanel } from '../../components/change-time-panel';
import { ICreateTimeBlocksRequest } from '../../../../entities-generated/models/time-block/create-time-blocks-request';
import { IChangeTimeBlocksRequest } from '../../../../entities-generated/models/time-block/change-time-blocks-request';
import { AdminTimeBlockApiClient } from '../../../../services/admin-time-block';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { MessageBarType } from '@fluentui/react';
import { TimeBlockList } from '../../components/time-list';

export const PageManageTimeBlocks: React.FunctionComponent = () => {
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [timeBlocks, setTimeBlocks] = useState<ITimeBlockListing[]>([]);
  const [launchCreateTimeBlocksArgs, setLaunchCreateTimeBlocksArgs] = useState<{} | undefined>(undefined);
  const [launchChangeTimeBlocksArgs, setLaunchChangeTimeBlocksArgs] = useState<{ startBlock: number, endBlock: number } | undefined>(undefined);
  const { showToast } = useToast();
  const [selectedBlocks, setSelectedBlocks] = useState<[number, number] | null>(null);
  const [clearSelectionApi, setClearSelectionApi] = useState<{} | undefined>(undefined);

  const refreshTimeBlocks = useCallback(() => {
    setViewState(BwjViewState.Loading);

    AdminTimeBlockApiClient.getManageableTimeBlocks()
    .then(resp =>  {
      setTimeBlocks(resp);
      const vs = resp.length === 0 ? BwjViewState.NoSearchResults : BwjViewState.Ready;
      setViewState(vs);
    })
    .catch(() => setViewState(BwjViewState.Error));
  }, []);

  const onLaunchTimeBlockCreator = () => setLaunchCreateTimeBlocksArgs({});

  const onCreateTimeBlocks = (request: ICreateTimeBlocksRequest) => {
    setViewState(BwjViewState.Loading);

    AdminTimeBlockApiClient.createTimeBlocks(request)
    .then(resp => {
      setTimeBlocks(resp);
    })
    .catch((e) => {
      console.log(e);
      showToast(MessageBarType.error, 'An unexpected error occurred');
    })
    .finally(() => setViewState(BwjViewState.Ready));
  };

  const onLaunchTimeBlockEditor = useCallback(() => {
    if(!selectedBlocks) { return; } // will never happen

    const args = {
      startBlock: selectedBlocks[0],
      endBlock: selectedBlocks[1]
    };
    setLaunchChangeTimeBlocksArgs(args);
  }, [selectedBlocks]);

  const onChangeTimeBlocks = (request: IChangeTimeBlocksRequest) => {
    setViewState(BwjViewState.Loading);

    AdminTimeBlockApiClient.updateTimeBlocks(request)
    .then(resp => {
      setTimeBlocks(resp);
    })
    .catch((e) => {
      console.log(e);
      showToast(MessageBarType.error, 'An unexpected error occurred');
    })
    .finally(() => setViewState(BwjViewState.Ready));
  };
  
  const actionButtons = useMemo(() => {
    const abs: IActionButtonInfo[] = [];
    if(!!selectedBlocks) {
      abs.push({
        id: 'btnClear',
        name: 'Clear selection',
        iconName: 'Clear',
        onClick: () => setClearSelectionApi({})
      });
      abs.push({
        id: 'btnChange',
        name: 'Change selected blocks',
        iconName: 'Edit',
        onClick: onLaunchTimeBlockEditor
      });
    }
    abs.push({
      id: 'btnRefresh',
      name: 'Refresh all',
      iconName: 'Refresh',
      onClick: refreshTimeBlocks
    });
    abs.push({
      id: 'btnCreate',
      name: 'Create new time blocks',
      iconName: 'Add',
      onClick: onLaunchTimeBlockCreator
    });

    return abs;
  }, [selectedBlocks, refreshTimeBlocks, onLaunchTimeBlockEditor]);

  useEffect(() => refreshTimeBlocks(), [refreshTimeBlocks]);

  const onSelectionChange = useCallback((selectionRange: [number, number] | null) => {
    setSelectedBlocks(selectionRange);
  }, []);

  return (
    <>
    <CreateTimeBlocksPanel
      invokePanelDisplay={launchCreateTimeBlocksArgs}
      onCreateTimeBlocks={onCreateTimeBlocks}
    />
    <ChangeTimeBlocksPanel
      launchPanelArgs={launchChangeTimeBlocksArgs}
      onChangeTimeBlocks={onChangeTimeBlocks}
    />
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Loading time blocks...'
      }}
      errorState={{
        message: 'An unexpected error occurred'
      }}
      noSearchResultsState={{
        icon: AppRoutes.manageTimeBlocks.icon,
        message: 'No time blocks available',
        buttons: [{
          buttonStyle: BwjButtonType.PrimaryStandard,
          text: 'Create time blocks',
          onClick: onLaunchTimeBlockCreator
        }],
      }}
    >
      <BwjPageHeader
        title={'Time Blocks'}
        titleIconName={AppRoutes.manageTimeBlocks.icon}
        actionButtons={actionButtons}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        <TimeBlockList
          timeBlocks={timeBlocks}
          onSelectionChange={onSelectionChange}
          clearSelectionApi={clearSelectionApi}
        />
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};
